import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { type FC, useMemo } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import { useMarketCalculator } from "~/feature/event/providers/MarketCalculatorProvider";
import type { IMarket } from "~/types/event";

const LOW_LIQUIDITY_TOOLTIP =
  "Low liquidity — high slippage risk on market orders.\nConsider a limit order.";
const ILLIQUID_TOOLTIP =
  "Illiquid market — no orders available on the other side.\nYour order may not fill.";
const LOW_LIQUIDITY = 100;
const LOW_LIQUIDITY_MULTIPLIER = 10;

const iconStyles = { fontSize: "11px", margin: "2px 0 0 2px" };
const iconLeftStyles = { fontSize: "11px", margin: "2px 2px 0 0" };

interface IMarketLowLiquidityProps {
  market: IMarket;
}

export const MarketLowLiquidity: FC<IMarketLowLiquidityProps> = ({
  market,
}) => {
  const { betAmount } = useMarketCalculator();

  const isLowLiquidity = useMemo(() => {
    const isRelevant = market.liquidity !== null;
    const isLiquidityLow =
      (market.liquidity || 0) < LOW_LIQUIDITY ||
      (market.liquidity || 0) < betAmount * LOW_LIQUIDITY_MULTIPLIER;

    return isRelevant && isLiquidityLow;
  }, [betAmount, market.liquidity]);

  const isIlliquid = market.bestAsk === 1 || market.bestAsk === 0;

  if (isIlliquid) {
    return (
      <>
        <WarningIcon color="disabled" sx={iconLeftStyles} />
        Illiquid market
        <RichTooltip title={ILLIQUID_TOOLTIP}>
          <InfoOutlinedIcon color="disabled" sx={iconStyles} />
        </RichTooltip>
        {"\n"}
      </>
    );
  }

  if (isLowLiquidity) {
    return (
      <>
        <WarningIcon color="disabled" sx={iconLeftStyles} />
        Low liquidity
        <RichTooltip title={LOW_LIQUIDITY_TOOLTIP}>
          <InfoOutlinedIcon color="disabled" sx={iconStyles} />
        </RichTooltip>
        {"\n"}
      </>
    );
  }

  return null;
};
